import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { doSignInWithEmailAndPassword, doSignInWithGoogle } from '../../../firebase/auth';
import { useAuth } from '../../../contexts/authContext';
import Logo from '../../common/Logo';
import VectorBackground from '../../common/VectorBackground';

const Login = () => {
    const { userLoggedIn, appSettings } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const onSubmit = async (e) => {
        e.preventDefault();
        if(!isSigningIn) {
            setIsSigningIn(true);
            setErrorMessage('');
            try {
                await doSignInWithEmailAndPassword(email, password);
            } catch (error) {
                let friendlyMessage = 'An unexpected error occurred. Please try again.';
                switch (error.code) {
                    case 'auth/invalid-credential':
                        friendlyMessage = 'Invalid email or password. Please check your credentials and try again.';
                        break;
                    case 'auth/operation-not-allowed':
                        friendlyMessage = 'Sign-in method is not enabled. Please contact an administrator.';
                        break;
                    case 'auth/user-disabled':
                        friendlyMessage = 'This user account has been disabled.';
                        break;
                    default:
                        friendlyMessage = `An error occurred: ${error.message}`;
                        break;
                }
                setErrorMessage(friendlyMessage);
                setIsSigningIn(false);
            }
        }
    };

    const onGoogleSignIn = (e) => {
        e.preventDefault();
        if (!isSigningIn) {
            setIsSigningIn(true);
            setErrorMessage('');
            doSignInWithGoogle().catch(err => {
                let friendlyMessage = 'An unexpected error occurred during Google Sign-In.';
                switch (err.code) {
                   case 'auth/account-exists-with-different-credential':
                       friendlyMessage = 'An account already exists with the same email address but different sign-in credentials. Sign in using a provider associated with this email address.';
                       break;
                   case 'auth/popup-closed-by-user':
                       friendlyMessage = 'The sign-in window was closed. Please try again.';
                       break;
                    case 'auth/operation-not-allowed':
                        friendlyMessage = 'Google Sign-in is not enabled. Please contact an administrator.';
                        break;
                   default:
                       friendlyMessage = `Google Sign-In Error: ${err.message}`;
               }
               setErrorMessage(friendlyMessage);
               setIsSigningIn(false);
            });
        }
    };

    return (
        <div className="relative w-full h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
            {userLoggedIn && (<Navigate to={'/home'} replace={true} />)}

            <VectorBackground />
            
            <main className="relative z-10 w-full max-w-md p-8 space-y-6 bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg border border-white/20 dark:border-white/10 rounded-2xl shadow-2xl">
                <div className="text-center">
                    <Logo />
                    <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-gray-100">
                        Welcome Back
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Sign in to access your projects
                    </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-6">
                    <div>
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Email</label>
                        <input
                            type="email"
                            autoComplete='email'
                            required
                            value={email} onChange={(e) => { setEmail(e.target.value) }}
                            className="w-full mt-2 px-4 py-2 text-gray-700 dark:text-gray-200 bg-white/20 dark:bg-gray-800/20 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Password</label>
                        <input
                            type="password"
                            autoComplete='current-password'
                            required
                            value={password} onChange={(e) => { setPassword(e.target.value) }}
                            className="w-full mt-2 px-4 py-2 text-gray-700 dark:text-gray-200 bg-white/20 dark:bg-gray-800/20 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-300"
                        />
                    </div>

                    {errorMessage && (
                        <span className='text-red-500 font-bold text-sm text-center block'>{errorMessage}</span>
                    )}

                    <button
                        type="submit"
                        disabled={isSigningIn}
                        className={`w-full px-4 py-3 text-white font-semibold rounded-lg transition-colors duration-300 ${isSigningIn ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {isSigningIn ? 'Signing In...' : 'Sign In'}
                    </button>
                </form>

                {appSettings.registrationEnabled && (
                    <>
                        <div className='flex items-center justify-center text-sm text-gray-500 dark:text-gray-400'>
                            <div className='flex-grow border-t border-gray-300 dark:border-gray-600'></div>
                            <span className='px-4 text-gray-500 dark:text-gray-400'>OR</span>
                            <div className='flex-grow border-t border-gray-300 dark:border-gray-600'></div>
                        </div>

                        <button
                            disabled={isSigningIn}
                            onClick={onGoogleSignIn}
                            className={`w-full flex items-center justify-center gap-x-3 py-2.5 border rounded-lg text-sm font-medium transition-colors duration-300 ${isSigningIn ? 'cursor-not-allowed bg-gray-100 dark:bg-gray-700' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                            <svg className="w-5 h-5" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_17_40)">
                                    <path d="M47.532 24.5528C47.532 22.9214 47.3997 21.2811 47.1175 19.6761H24.48V28.9181H37.4434C36.9055 31.8988 35.177 34.5356 32.6461 36.2111V42.2078H40.3801C44.9217 38.0278 47.532 31.8547 47.532 24.5528Z" fill="#4285F4" />
                                    <path d="M24.48 48.0016C30.9529 48.0016 36.4116 45.8764 40.3888 42.2078L32.6549 36.2111C30.5031 37.675 27.7252 38.5039 24.4888 38.5039C18.2275 38.5039 12.9187 34.2798 11.0139 28.6006H3.03296V34.7825C7.10718 42.8868 15.4056 48.0016 24.48 48.0016Z" fill="#34A853" />
                                    <path d="M11.0051 28.6006C9.99973 25.6199 9.99973 22.3922 11.0051 19.4115V13.2296H3.03298C-0.371021 20.0012 -0.371021 28.0009 3.03298 34.7825L11.0051 28.6006Z" fill="#FBBC04" />
                                    <path d="M24.48 9.49932C27.9016 9.44641 31.2086 10.7339 33.6866 13.0973L40.5387 6.24523C36.2 2.17101 30.4414 -0.068932 24.48 0.00161733C15.4055 0.00161733 7.10718 5.11644 3.03296 13.2296L11.005 19.4115C12.901 13.7235 18.2187 9.49932 24.48 9.49932Z" fill="#EA4335" />
                                </g>
                                <defs><clipPath id="clip0_17_40"><rect width="48" height="48" fill="white" /></clipPath></defs>
                            </svg>
                            Continue with Google
                        </button>

                        <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                            Don't have an account?{' '}
                            <Link to={'/register'} className="font-semibold text-blue-600 hover:underline dark:text-blue-400">
                                Sign up
                            </Link>
                        </p>
                    </>
                )}
            </main>
        </div>
    );
}

export default Login;
